import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './SuperAdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Organization {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

const SuperAdminPanel: React.FC = () => {
  const { user, organization: currentOrg, setOrganization, login } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Create org form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const fetchOrganizations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/organizations`);
      if (!response.ok) throw new Error('Failed to fetch organizations');
      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim() || !user?.email) return;

    setIsCreating(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/organizations?admin_email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newOrgName.trim() }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create organization');
      }

      // Refresh the list
      setNewOrgName('');
      setShowCreateForm(false);
      await fetchOrganizations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectOrganization = async (org: Organization) => {
    if (!user?.email) return;

    try {
      // Add super admin to org if not already
      await fetch(`${API_URL}/organizations/${org.id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          role: 'admin',
        }),
      });

      // Set as current organization
      setOrganization({
        id: org.id,
        name: org.name,
        slug: org.slug,
        createdAt: org.createdAt,
        updatedAt: org.createdAt,
      });

      // Re-login to refresh user data with new organizationId
      await login({
        id: user.id,
        email: user.email,
        name: user.name,
        picture: user.picture,
      });

    } catch (err) {
      console.error('Error selecting org:', err);
      setError('Failed to select organization');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="super-admin-panel">
      <div className="panel-header">
        <div className="panel-title">
          <span className="super-badge">⚡ Super Admin</span>
          <h3>Organization Management</h3>
        </div>
        <button
          className="create-org-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Create Organization'}
        </button>
      </div>

      {error && (
        <div className="panel-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {showCreateForm && (
        <form className="create-org-form" onSubmit={handleCreateOrganization}>
          <input
            type="text"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            placeholder="Organization name"
            disabled={isCreating}
            autoFocus
          />
          <button type="submit" disabled={isCreating || !newOrgName.trim()}>
            {isCreating ? 'Creating...' : 'Create'}
          </button>
        </form>
      )}

      <div className="org-list">
        {isLoading ? (
          <div className="org-loading">Loading organizations...</div>
        ) : organizations.length === 0 ? (
          <div className="org-empty">
            <p>No organizations yet.</p>
            <p>Create your first organization to get started.</p>
          </div>
        ) : (
          <table className="org-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Slug</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => {
                const isSelected = currentOrg?.id === org.id;
                return (
                  <tr key={org.id} className={isSelected ? 'selected-row' : ''}>
                    <td className="org-name">
                      {org.name}
                      {isSelected && <span className="current-badge">Current</span>}
                    </td>
                    <td className="org-slug">{org.slug}</td>
                    <td className="org-date">{formatDate(org.createdAt)}</td>
                    <td>
                      {isSelected ? (
                        <span className="selected-indicator">✓ Selected</span>
                      ) : (
                        <button
                          className="select-org-btn"
                          onClick={() => handleSelectOrganization(org)}
                        >
                          Select & Manage
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SuperAdminPanel;
